import { usePdfStore } from '@/stores/pdfStore';
import { PDF_TOOLS, type PdfToolId } from '@/data/pdfTools';
import PdfSignaturesPanel from './PdfSignaturesPanel';

function toolDef(id: PdfToolId) {
  return PDF_TOOLS.find((t) => t.id === id);
}

interface PdfToolOptionsPanelProps {
  userId: string;
  /** Compact bottom plate for phone; rail is the desktop right column. */
  variant?: 'rail' | 'plate';
  /** Phone: open full signature library in a sheet instead of inlining it. */
  onOpenSignatures?: () => void;
}

export default function PdfToolOptionsPanel({
  userId,
  variant = 'rail',
  onOpenSignatures,
}: PdfToolOptionsPanelProps) {
  const tool = usePdfStore((s) => s.tool);
  const color = usePdfStore((s) => s.color);
  const highlightColor = usePdfStore((s) => s.highlightColor);
  const brushSize = usePdfStore((s) => s.brushSize);
  const fontSize = usePdfStore((s) => s.fontSize);
  const shapeKind = usePdfStore((s) => s.shapeKind);
  const selectedSignatureId = usePdfStore((s) => s.selectedSignatureId);
  const signatures = usePdfStore((s) => s.signatures);
  const setColor = usePdfStore((s) => s.setColor);
  const setHighlightColor = usePdfStore((s) => s.setHighlightColor);
  const setBrushSize = usePdfStore((s) => s.setBrushSize);
  const setFontSize = usePdfStore((s) => s.setFontSize);
  const setShapeKind = usePdfStore((s) => s.setShapeKind);
  const setSignatureModalOpen = usePdfStore((s) => s.setSignatureModalOpen);

  const isPlate = variant === 'plate';
  const def = toolDef(tool);

  if (tool === 'sign' && !isPlate) {
    return <PdfSignaturesPanel userId={userId} />;
  }

  if (tool === 'sign' && isPlate) {
    const selected = signatures.find((s) => s.signatureId === selectedSignatureId);
    return (
      <aside className="pdfe-options pdfe-options-plate" aria-label="Signature properties">
        <div className="pdfe-plate-head">
          <strong>Sign</strong>
          <span className="pdfe-options-hint">
            {selected ? `Selected: ${selected.name}` : 'Choose or create a signature'}
          </span>
        </div>
        <div className="pdfe-plate-row">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onOpenSignatures?.()}
          >
            Library
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setSignatureModalOpen(true)}
          >
            Create
          </button>
        </div>
      </aside>
    );
  }

  if (!def?.hasOptions) {
    if (isPlate) {
      return (
        <aside className="pdfe-options pdfe-options-plate pdfe-options-plate-hint">
          <p className="pdfe-options-hint">{def?.hint ?? 'Select a tool to get started.'}</p>
        </aside>
      );
    }
    return (
      <aside className="pdfe-options">
        <div className="pdfe-options-empty">
          <div className="pdfe-options-empty-art" aria-hidden>
            <span className="pdfe-options-empty-stack" />
            <span className="pdfe-options-empty-pen" />
          </div>
          <p>Select a tool to get started.</p>
        </div>
      </aside>
    );
  }

  const fields = (
    <>
      {(tool === 'brush' || tool === 'shapes' || tool === 'text') && (
        <label className="pdfe-field">
          <span>Color</span>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
        </label>
      )}

      {tool === 'highlight' && (
        <label className="pdfe-field">
          <span>Highlight</span>
          <input
            type="color"
            value={highlightColor}
            onChange={(e) => setHighlightColor(e.target.value)}
          />
        </label>
      )}

      {(tool === 'brush' || tool === 'highlight') && (
        <label className="pdfe-field">
          <span>Size</span>
          <input
            type="range"
            min={1}
            max={tool === 'highlight' ? 64 : 32}
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
          />
          <em>{brushSize}px</em>
        </label>
      )}

      {tool === 'text' && (
        <label className="pdfe-field">
          <span>Font size</span>
          <input
            type="range"
            min={10}
            max={72}
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
          />
          <em>{fontSize}px</em>
        </label>
      )}

      {tool === 'shapes' && (
        <div className="pdfe-field">
          <span>Shape</span>
          <div className="pdfe-seg">
            <button
              type="button"
              className={shapeKind === 'rect' ? 'is-active' : ''}
              onClick={() => setShapeKind('rect')}
            >
              Rectangle
            </button>
            <button
              type="button"
              className={shapeKind === 'ellipse' ? 'is-active' : ''}
              onClick={() => setShapeKind('ellipse')}
            >
              Ellipse
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (isPlate) {
    return (
      <aside className="pdfe-options pdfe-options-plate" aria-label={`${def.label} properties`}>
        <div className="pdfe-plate-head">
          <strong>{def.label}</strong>
          <span className="pdfe-options-hint">{def.hint}</span>
        </div>
        <div className="pdfe-plate-fields">{fields}</div>
      </aside>
    );
  }

  return (
    <aside className="pdfe-options">
      <h2>{def.label}</h2>
      <p className="pdfe-options-hint">{def.hint}</p>
      {fields}
    </aside>
  );
}
